import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Callout,
  Label,
  LoadingSpinner,
  LockIcon,
  SaveIcon,
  TextInput,
} from '../components/ui';
import { useAuth } from '../hooks/useAuth';
import { t } from '../i18n';
import { ApiError, api } from '../services/api';

export function ResetPasswordPage() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { establishSession } = useAuth();
  const labels = t().resetPassword;
  const [email, setEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getPasswordReset(token);
        if (!cancelled) setEmail(data.email);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof ApiError ? err.message : labels.invalidLink,
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [token, labels.invalidLink]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError(labels.passwordMin);
      return;
    }
    if (password !== confirm) {
      setError(labels.passwordMismatch);
      return;
    }

    setSaving(true);
    try {
      const result = await api.resetPassword(token, password);
      establishSession(result.accessToken, result.user);
      if (result.user.role === 'SUPER_ADMIN') {
        navigate('/platform/tenants', { replace: true });
      } else {
        navigate(
          result.user.tenantSetupCompleted ? '/' : '/setup/company',
          { replace: true },
        );
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface p-4 text-ink">
      <div className="w-full max-w-md rounded-xl border border-line bg-panel p-6 shadow-bf-sm sm:p-8">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-500/15 text-teal-700 dark:text-teal-300">
            <LockIcon className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold">
              {labels.title}
            </h1>
            <p className="text-sm text-muted">{labels.hint}</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <LoadingSpinner />
          </div>
        ) : error && !email ? (
          <div className="space-y-4">
            <Callout variant="danger">{error}</Callout>
            <Link
              to="/forgot-password"
              className="inline-block text-sm font-medium text-teal-700 hover:underline dark:text-teal-300"
            >
              {labels.requestAgain}
            </Link>
            <div>
              <Link
                to="/login"
                className="inline-block text-sm font-medium text-teal-700 hover:underline dark:text-teal-300"
              >
                {labels.backToLogin}
              </Link>
            </div>
          </div>
        ) : (
          <form className="space-y-4" onSubmit={handleSubmit}>
            {email ? (
              <div className="rounded-lg border border-line bg-surface/50 px-3 py-3 text-sm">
                <span className="text-muted">{t().auth.email}: </span>
                <span className="font-medium">{email}</span>
              </div>
            ) : null}

            {error ? <Callout variant="danger">{error}</Callout> : null}

            <div>
              <Label htmlFor="reset-password" required>
                {labels.password}
              </Label>
              <TextInput
                id="reset-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="reset-confirm" required>
                {labels.confirmPassword}
              </Label>
              <TextInput
                id="reset-confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
              />
            </div>

            <Button type="submit" className="w-full" loading={saving}>
              <SaveIcon className="h-4 w-4" />
              {labels.submit}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
